import { useState, type FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Input } from '../../components/ui/input.tsx';
import { Label } from '../../components/ui/label.tsx';
import { useCreateLink, useUploadAvatar, type CreateLinkBody } from '../../lib/hooks.ts';
import { ApiError } from '../../lib/api.ts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  theoryId: string;
}

export function CreateTrackingLinkDialog({ open, onOpenChange, theoryId }: Props): JSX.Element {
  const [form, setForm] = useState({
    slug: '',
    source: '',
    destination_url: '',
    channel_title: '',
    channel_avatar_url: '',
    channel_description: '',
    cta_label: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const create = useCreateLink();
  const uploadAvatar = useUploadAvatar();

  async function onAvatarChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    e.target.value = ''; // позволяем повторно выбрать тот же файл после ошибки
    if (!file) return;
    setUploadError(null);
    try {
      const { url } = await uploadAvatar.mutateAsync(file);
      setForm((f) => ({ ...f, channel_avatar_url: url }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 413) {
        setUploadError('Файл слишком большой. Лимит 2 МБ.');
      } else if (err instanceof ApiError && err.status === 422) {
        setUploadError('Неподдерживаемый формат. Только JPEG, PNG, WebP.');
      } else {
        setUploadError(err instanceof Error ? err.message : 'Не удалось загрузить файл');
      }
    }
  }

  function reset(): void {
    setForm({
      slug: '',
      source: '',
      destination_url: '',
      channel_title: '',
      channel_avatar_url: '',
      channel_description: '',
      cta_label: '',
    });
    setError(null);
  }

  function handleClose(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const body: CreateLinkBody = {
      theory_id: theoryId,
      slug: form.slug.trim(),
      source: form.source.trim(),
      destination_url: form.destination_url.trim(),
    };
    if (form.channel_title.trim()) body.channel_title = form.channel_title.trim();
    if (form.channel_avatar_url.trim()) body.channel_avatar_url = form.channel_avatar_url.trim();
    if (form.channel_description.trim()) body.channel_description = form.channel_description.trim();
    if (form.cta_label.trim()) body.cta_label = form.cta_label.trim();

    try {
      await create.mutateAsync(body);
      reset();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'slug_conflict') {
        setError('Идентификатор уже занят (slug уникален во всей системе).');
      } else if (err instanceof ApiError && err.status === 422) {
        setError('Проверь поля: slug — латиница и дефисы, URL — полный с https://.');
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось создать ссылку');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <form onSubmit={onSubmit}>
          <DialogTitle>Новая tracking-ссылка</DialogTitle>
          <DialogDescription>
            Эту ссылку (вида /c/&lt;slug&gt;) ты вставляешь в шапку IG или другой источник.
            Channel-поля рендерятся на лендинге как preview Telegram-канала.
          </DialogDescription>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="link-slug">Идентификатор</Label>
              <Input
                id="link-slug"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))}
                placeholder="ig-bio-march"
                required
                maxLength={100}
                pattern="^[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?$"
                className="font-mono"
                autoFocus
              />
              <p className="text-xs text-fg-muted">
                Только латиница, цифры и дефис. Заглавные приводятся к нижнему регистру
                автоматически. Уникален во всей системе.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="link-source">Источник</Label>
              <Input
                id="link-source"
                value={form.source}
                onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                placeholder="instagram_bio"
                required
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="link-destination">Куда отправлять (Telegram URL)</Label>
              <Input
                id="link-destination"
                value={form.destination_url}
                onChange={(e) => setForm((f) => ({ ...f, destination_url: e.target.value }))}
                placeholder="https://t.me/+ABCxyz123"
                required
                type="url"
                maxLength={2000}
              />
            </div>

            <div className="sm:col-span-2 mt-2 border-t border-border pt-3">
              <p className="mb-2 text-xs text-fg-muted">
                Channel-preview для лендинга (опционально).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="link-channel-title">Название канала</Label>
              <Input
                id="link-channel-title"
                value={form.channel_title}
                onChange={(e) => setForm((f) => ({ ...f, channel_title: e.target.value }))}
                placeholder="Self-Worth Channel"
                maxLength={200}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="link-channel-avatar">Аватарка</Label>
              <div className="flex items-center gap-2">
                {form.channel_avatar_url ? (
                  <img
                    src={form.channel_avatar_url}
                    alt="preview"
                    className="h-10 w-10 rounded-full border border-border object-cover"
                  />
                ) : null}
                <label
                  htmlFor="link-channel-avatar-file"
                  className="btn-secondary cursor-pointer text-xs"
                >
                  {uploadAvatar.isPending
                    ? 'Загружаем…'
                    : form.channel_avatar_url
                      ? 'Заменить файлом'
                      : 'Выбрать файл'}
                </label>
                <input
                  id="link-channel-avatar-file"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => void onAvatarChange(e)}
                />
              </div>
              <Input
                id="link-channel-avatar"
                value={form.channel_avatar_url}
                onChange={(e) => setForm((f) => ({ ...f, channel_avatar_url: e.target.value }))}
                placeholder="https://cdn.example.com/avatar.png (или загрузи файл выше)"
                type="url"
                maxLength={2000}
              />
              {uploadError ? <p className="text-xs text-danger">{uploadError}</p> : null}
              <p className="text-xs text-fg-muted">
                JPEG, PNG или WebP до 2 МБ. URL подставится автоматически после загрузки.
              </p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="link-channel-description">Описание канала</Label>
              <Input
                id="link-channel-description"
                value={form.channel_description}
                onChange={(e) => setForm((f) => ({ ...f, channel_description: e.target.value }))}
                placeholder="Daily reflections about self-worth"
                maxLength={2000}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="link-cta-label">Текст кнопки</Label>
              <Input
                id="link-cta-label"
                value={form.cta_label}
                onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))}
                placeholder="Вступить в Telegram"
                maxLength={50}
              />
              <p className="text-xs text-fg-muted">
                Пусто — будет дефолт «Вступить в Telegram». До 50 символов.
              </p>
            </div>
          </div>

          {error ? (
            <p className="mt-3 rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
              Отмена
            </Button>
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? 'Создаём…' : 'Создать'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
